/**
 * JourneyTimeline - Visual Gantt Chart for Return Journey
 *
 * Displays a visual timeline showing:
 * - Pre-return phase
 * - Return date marker
 * - RNOR window (2-3 years)
 * - Settlement phase
 * - Key milestones with dates
 * - Current date indicator
 */

import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface TimelineMilestone {
  id: string;
  date: string;
  label: string;
  type: 'start' | 'return' | 'rnor_end' | 'fire_target' | 'milestone';
  description?: string;
}

interface JourneyTimelineProps {
  targetReturnDate: string; // ISO date string
  rnorEndDate?: string; // ISO date string
  milestones?: TimelineMilestone[];
  rnorYears?: number;
  className?: string;
}

// Phase colors - Consolidated brand palette (no purple)
const PHASE_STYLES = {
  preReturn: {
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-700',
    label: 'Pre-Return',
    icon: '🛫',
  },
  rnor: {
    bg: 'bg-primary-100',
    border: 'border-primary-300',
    text: 'text-primary-700',
    label: 'RNOR Window',
    icon: '🛡️',
  },
  settlement: {
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-600',
    label: 'Settlement',
    icon: '🏠',
  },
};

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function JourneyTimeline({
  targetReturnDate,
  rnorEndDate,
  milestones = [],
  rnorYears = 2,
  className,
}: JourneyTimelineProps) {
  // Collapsed by default to reduce visual length
  const [isExpanded, setIsExpanded] = useState(false);
  // Calculate timeline bounds
  const timelineData = useMemo(() => {
    const now = new Date();
    const returnDate = new Date(targetReturnDate);

    // Calculate RNOR end if not provided
    const rnorEnd = rnorEndDate
      ? new Date(rnorEndDate)
      : new Date(returnDate.getTime() + rnorYears * 365 * 24 * 60 * 60 * 1000);

    // Timeline spans from current year to RNOR end + 1 year
    const startYear = now.getFullYear();
    const endYear = rnorEnd.getFullYear() + 1;
    const totalYears = endYear - startYear;

    // Calculate positions as percentages
    const yearToPercent = (year: number) => ((year - startYear) / totalYears) * 100;
    const dateToPercent = (date: Date) => {
      const yearProgress = (date.getMonth() / 12);
      return yearToPercent(date.getFullYear()) + (yearProgress / totalYears) * 100;
    };

    return {
      startYear,
      endYear,
      totalYears,
      nowPercent: dateToPercent(now),
      returnPercent: dateToPercent(returnDate),
      rnorEndPercent: dateToPercent(rnorEnd),
      returnDate,
      rnorEndDate: rnorEnd,
      yearToPercent,
      dateToPercent,
    };
  }, [targetReturnDate, rnorEndDate, rnorYears]);

  // Generate year markers
  const yearMarkers = useMemo(() => {
    const markers = [];
    for (let year = timelineData.startYear; year <= timelineData.endYear; year++) {
      markers.push({
        year,
        percent: timelineData.yearToPercent(year),
      });
    }
    return markers;
  }, [timelineData]);

  // Check if we have data
  if (!targetReturnDate) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <span>📅</span>
              <span>Your Return Timeline</span>
            </CardTitle>
            <p className="text-sm text-slate-500">
              Visual overview of your journey phases and key milestones
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Quick summary when collapsed */}
            {!isExpanded && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-600 font-medium">
                  {formatDate(targetReturnDate)}
                </span>
                <span className="text-slate-400">→</span>
                <span className="text-primary-600 font-medium">{rnorYears}yr RNOR</span>
              </div>
            )}
            <span className={cn(
              'text-slate-400 transition-transform',
              isExpanded && 'rotate-180'
            )}>▼</span>
          </div>
        </div>
      </CardHeader>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
      <CardContent>
        {/* Timeline Container */}
        <div className="relative">
          {/* Year markers */}
          <div className="flex justify-between mb-2 text-xs text-slate-400">
            {yearMarkers.map(({ year, percent }) => (
              <div
                key={year}
                className="text-center"
                style={{ position: 'absolute', left: `${percent}%`, transform: 'translateX(-50%)' }}
              >
                {year}
              </div>
            ))}
          </div>

          {/* Main timeline bar */}
          <div className="relative h-16 mt-6 mb-4">
            {/* Background track */}
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-3 bg-slate-100 rounded-full" />

            {/* Pre-return phase */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${timelineData.returnPercent}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 h-3 rounded-l-full left-0',
                PHASE_STYLES.preReturn.bg
              )}
            />

            {/* RNOR phase */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${timelineData.rnorEndPercent - timelineData.returnPercent}%` }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 h-3',
                PHASE_STYLES.rnor.bg
              )}
              style={{ left: `${timelineData.returnPercent}%` }}
            />

            {/* Settlement phase */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${100 - timelineData.rnorEndPercent}%` }}
              transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 h-3 rounded-r-full',
                PHASE_STYLES.settlement.bg
              )}
              style={{ left: `${timelineData.rnorEndPercent}%` }}
            />

            {/* Current date marker */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1 }}
              className="absolute top-0 flex flex-col items-center"
              style={{ left: `${timelineData.nowPercent}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-4 h-4 bg-primary-500 rounded-full border-2 border-white shadow-md" />
              <div className="w-0.5 h-4 bg-primary-500" />
              <span className="text-[10px] font-semibold text-primary-600 whitespace-nowrap mt-0.5">
                TODAY
              </span>
            </motion.div>

            {/* Return date marker */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute bottom-0 flex flex-col items-center"
              style={{ left: `${timelineData.returnPercent}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[10px] font-semibold text-emerald-600 whitespace-nowrap mb-0.5">
                RETURN
              </span>
              <div className="w-0.5 h-4 bg-emerald-500" />
              <div className="w-5 h-5 bg-emerald-500 rounded-full border-2 border-white shadow-md flex items-center justify-center">
                <span className="text-[10px]">✈️</span>
              </div>
            </motion.div>

            {/* RNOR end marker */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="absolute top-0 flex flex-col items-center"
              style={{ left: `${timelineData.rnorEndPercent}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-4 h-4 bg-amber-500 rounded-full border-2 border-white shadow-md" />
              <div className="w-0.5 h-4 bg-amber-500" />
              <span className="text-[10px] font-semibold text-amber-600 whitespace-nowrap mt-0.5">
                RNOR ENDS
              </span>
            </motion.div>
          </div>

          {/* Phase Legend */}
          <div className="flex flex-wrap gap-4 justify-center mt-6 mb-4">
            {Object.entries(PHASE_STYLES).map(([key, style]) => (
              <div key={key} className="flex items-center gap-2">
                <div className={cn('w-4 h-4 rounded', style.bg, style.border, 'border')} />
                <span className={cn('text-xs font-medium', style.text)}>
                  {style.icon} {style.label}
                </span>
              </div>
            ))}
          </div>

          {/* Key Dates Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-slate-50 rounded-lg p-3 border border-slate-200"
            >
              <div className="flex items-center gap-2 mb-1">
                <span>🛫</span>
                <span className="text-xs font-semibold text-slate-700 uppercase">Pre-Return</span>
              </div>
              <p className="text-sm text-slate-600">
                Now → {formatDate(targetReturnDate)}
              </p>
              <p className="text-xs text-slate-400">Preparation & optimization</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-primary-50 rounded-lg p-3 border border-primary-200"
            >
              <div className="flex items-center gap-2 mb-1">
                <span>🛡️</span>
                <span className="text-xs font-semibold text-primary-700 uppercase">RNOR Window</span>
              </div>
              <p className="text-sm text-slate-600">
                {formatDate(targetReturnDate)} → {formatDate(timelineData.rnorEndDate.toISOString())}
              </p>
              <p className="text-xs text-slate-400">{rnorYears}-year tax-free foreign income</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="bg-slate-50 rounded-lg p-3 border border-slate-200"
            >
              <div className="flex items-center gap-2 mb-1">
                <span>🏠</span>
                <span className="text-xs font-semibold text-slate-700 uppercase">Settlement</span>
              </div>
              <p className="text-sm text-slate-600">
                {formatDate(timelineData.rnorEndDate.toISOString())} onwards
              </p>
              <p className="text-xs text-slate-400">Full India resident taxation</p>
            </motion.div>
          </div>

          {/* Custom Milestones */}
          {milestones.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Key Milestones</h4>
              <div className="space-y-2">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 + index * 0.1 }}
                    className="flex items-center gap-3 text-sm"
                  >
                    <span className="text-slate-400">{formatDate(milestone.date)}</span>
                    <span className="w-2 h-2 rounded-full bg-slate-300" />
                    <span className="text-slate-600">{milestone.label}</span>
                    {milestone.description && (
                      <span className="text-xs text-slate-400">({milestone.description})</span>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Important Note */}
          <div className="mt-4 text-xs text-slate-400 bg-slate-50 rounded-lg p-3">
            <strong>Note:</strong> RNOR window duration depends on your residency history.
            Typical duration is 2-3 years. Consult a CA for exact eligibility dates.
          </div>
        </div>
      </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default JourneyTimeline;
